import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import AdminLectureForm from './AdminLectureForm';
import AdminMediaLibrary from './AdminMediaLibrary';
import AdminTestEditor from './AdminTestEditor';

// --- (FIXED) SortableLectureItem with a dedicated drag handle ---
const SortableLectureItem = ({ lecture, index, onEdit, onDelete }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: lecture.id });
    const style = { transform: CSS.Transform.toString(transform), transition };

    return (
        <tr ref={setNodeRef} style={style}>
            {/* The drag handle. The listeners are attached here. */}
            <td className="drag-handle" {...attributes} {...listeners}>
                <span className="material-icons">drag_indicator</span>
            </td>
            <td>{index + 1}</td>
            <td>{lecture.title}</td>
            <td>{lecture.lecture_type}</td>
            <td className="admin-table-actions">
                <div className="action-buttons-container">
                    {/* These buttons will now respond to clicks */}
                    <button onClick={() => onEdit(lecture)} className="action-btn edit" title="Edit"><span className="material-icons">edit</span></button>
                    <button onClick={() => onDelete(lecture.id)} className="action-btn delete" title="Delete"><span className="material-icons">delete_forever</span></button>
                </div>
            </td>
        </tr>
    );
};


const AdminCourseEditor = () => {
    const { courseId } = useParams();
    const [course, setCourse] = useState(null);
    const [lectures, setLectures] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    
    // State for modals
    const [isLectureFormOpen, setIsLectureFormOpen] = useState(false);
    const [isTestEditorOpen, setIsTestEditorOpen] = useState(false);
    const [editingLecture, setEditingLecture] = useState(null);
    const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);
    
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );
    
    const fetchCourse = async () => {
        try {
            const token = localStorage.getItem('app_token');
            const response = await axios.get(`/api/admin/courses/${courseId}`, { headers: { Authorization: `Bearer ${token}` } });
            setCourse(response.data);
            setLectures(response.data.lectures || []);
        } catch (error) {
            console.error("Failed to fetch course data", error);
        } finally {
            setIsLoading(false);
        }
    };
    
    
    useEffect(() => {
        fetchCourse();
    }, [courseId]);

    const handleOpenLectureEditor = (lecture) => {
        setEditingLecture(lecture);
        if (lecture && lecture.lecture_type === 'test') {
            setIsTestEditorOpen(true);
        } else {
            setIsLectureFormOpen(true);
        }
    };

    const handleCloseAllModals = () => {
        setIsLectureFormOpen(false);
        setIsTestEditorOpen(false);
        setEditingLecture(null);
    };

    const handleLectureSave = () => {
        fetchCourse(); 
        handleCloseAllModals();
    };
    
    const handleCourseChange = (e) => {
        const { name, value } = e.target;
        setCourse(prev => ({ ...prev, [name]: value }));
    };

    const handleDescriptionChange = (event, editor) => {
        setCourse(prev => ({ ...prev, description: editor.getData() }));
    };

    const handleCourseSave = async () => {
        try {
            const token = localStorage.getItem('app_token');
            await axios.put(`/api/admin/courses/${courseId}`, course, { headers: { Authorization: `Bearer ${token}` } });
            alert('Course details saved!');
        } catch (error) {
            alert('Failed to save course details.');
        }
    };

    const handleLectureDelete = async (lectureId) => {
        if (window.confirm('Are you sure you want to delete this lecture? This will also delete any associated test.')) {
            const lectureToDelete = lectures.find(l => l.id === lectureId);
            if(lectureToDelete?.title.toLowerCase() === 'overview'){
                alert("The 'Overview' lecture cannot be deleted.");
                return;
            }
            try {
                const token = localStorage.getItem('app_token');
                await axios.delete(`/api/admin/lectures/${lectureId}`, { headers: { Authorization: `Bearer ${token}` } });
                fetchCourse();
            } catch (error) {
                alert('Failed to delete lecture.');
            }
        }
    };
    
    const handleDragEnd = async (event) => {
        const { active, over } = event;
        if (active.id !== over.id) {
            const oldIndex = lectures.findIndex((item) => item.id === active.id);
            const newIndex = lectures.findIndex((item) => item.id === over.id);
            const reorderedLectures = arrayMove(lectures, oldIndex, newIndex);
            setLectures(reorderedLectures);

            const lectureIds = reorderedLectures.map(item => item.id);
            
            try {
                const token = localStorage.getItem('app_token');
                await axios.put(`/api/admin/courses/${courseId}/lectures/order`, 
                    { lectureIds }, 
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            } catch (error) {
                alert('Failed to save the new lecture order. Please refresh the page.');
                fetchCourse();
            }
        }
    };
    
    if (isLoading) return <p>Loading course editor...</p>;
    if (!course) return <p>Could not load course data.</p>;

    return (
        <>
            {isLectureFormOpen && (
                <AdminLectureForm
                    courseId={courseId}
                    lecture={editingLecture}
                    onSave={handleLectureSave}
                    onCancel={handleCloseAllModals}
                    nextOrder={lectures.length}
                />
            )}
            {isTestEditorOpen && editingLecture && (
                <AdminTestEditor
                    lectureId={editingLecture.id}
                    lectureTitle={editingLecture.title}
                    onSave={handleLectureSave}
                    onCancel={handleCloseAllModals}
                />
            )}
            {isMediaLibraryOpen && (
                <AdminMediaLibrary 
                    onSelect={(url) => {
                        setCourse(prev => ({ ...prev, thumbnail: url }));
                        setIsMediaLibraryOpen(false);
                    }}
                    onCancel={() => setIsMediaLibraryOpen(false)}
                    s3Prefix="hj1/public/assets/images/"
                />
            )}

            <div className="admin-content-manager">
                 <Link to="/admin/courses" className="sidebar-link" style={{ marginBottom: '1rem', display: 'inline-flex' }}>
                    <span className="material-icons">arrow_back</span>
                    Back to All Courses
                </Link>
                <h3 className="admin-section-title">Edit Course: {course.title}</h3>
                
                <div className="admin-form" style={{ background: '#fff', padding: '2rem', borderRadius: '12px' }}>
                    <h4>Course Details</h4>
                    <div className="form-group"><label>Title</label><input type="text" name="title" value={course.title} onChange={handleCourseChange} /></div>
                    <div className="form-group"><label>Category</label><input type="text" name="category" value={course.category} onChange={handleCourseChange} /></div>
                    <div className="form-group"><label>Price (₹)</label><input type="number" name="price" value={course.price || '0.00'} onChange={handleCourseChange} step="0.01" /></div>
                    <div className="form-group">
                        <label>Thumbnail</label>
                         <div className="media-input-group">
                            <input type="text" name="thumbnail" value={course.thumbnail || ''} onChange={handleCourseChange} readOnly />
                            <button type="button" className="btn-secondary" onClick={() => setIsMediaLibraryOpen(true)}>Select Image</button>
                        </div>
                    </div>
                    <div className="form-group"><label>Description</label><CKEditor editor={ClassicEditor} data={course.description || ''} onChange={handleDescriptionChange} /></div>
                    <button onClick={handleCourseSave} className="btn-primary">Save Course Details</button>
                </div>
                
                <div style={{ marginTop: '2rem' }}>
                    <div className="admin-content-header">
                        <h4>Course Curriculum</h4>
                        <button onClick={() => handleOpenLectureEditor(null)} className="btn-primary">Add Lecture</button>
                    </div>
                    <div className="admin-table-container">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th style={{width: '50px'}}></th>
                                    <th>Order</th>
                                    <th>Title</th>
                                    <th>Type</th>
                                    <th className="admin-actions-column">Actions</th>
                                </tr>
                            </thead>
                            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                <SortableContext items={lectures} strategy={verticalListSortingStrategy}>
                                    <tbody>
                                        {lectures.map((lecture, index) => (
                                            <SortableLectureItem 
                                                key={lecture.id}
                                                lecture={lecture} 
                                                index={index}
                                                onEdit={handleOpenLectureEditor}
                                                onDelete={handleLectureDelete}
                                            />
                                        ))}
                                    </tbody>
                                </SortableContext>
                            </DndContext>
                        </table>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AdminCourseEditor;
